import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, ArrowsClockwise, ArrowSquareOut, Plus, Ruler } from "@phosphor-icons/react";
import { AdminHeader, GhostButton, inputCls } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { VersionEditor } from "@/components/admin/model-specs/VersionEditor";

const genLabel = (g) => (g ? `Gen ${g}` : "Current");

function ModelList({ specs, selected, onSelect }) {
  const [q, setQ] = useState("");
  const shown = specs.filter((s) => s.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-3 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] flex flex-col">
      <div className="flex items-center gap-2 bg-black/40 border border-neutral-800 rounded-lg px-3 py-2 mb-2 focus-within:border-[#73bf44]">
        <MagnifyingGlass size={14} className="text-neutral-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search models…" data-testid="ms-model-search" className="bg-transparent outline-none text-sm text-white w-full placeholder:text-neutral-600" />
      </div>
      <div className="overflow-y-auto space-y-0.5 -mr-1 pr-1">
        {shown.map((s) => (
          <button key={s.id} onClick={() => onSelect(s.id)} data-testid="ms-model-item" className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors ${selected === s.id ? "bg-[#73bf44]/10 text-[#73bf44]" : "text-neutral-300 hover:bg-white/[0.03]"}`}>
            <div className="text-sm font-medium truncate">{s.name}</div>
            <div className="flex items-center gap-1.5 mt-1 text-[0.65rem] text-neutral-500">
              {s.vans} vans · website {genLabel(s.current_generation)}
              {s.generations_entered.filter((g) => g !== (s.current_generation || 0)).map((g) => (
                <span key={g} className="px-1.5 rounded bg-[#73bf44]/15 text-[#73bf44]">G{g}</span>
              ))}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function GenerationPicker({ spec, maxGen, onChange }) {
  const value = spec.current_generation_manual ? String(spec.current_generation) : "auto";
  return (
    <label className="flex items-center gap-2 text-sm text-neutral-400">
      Website (MY26) shows
      <select value={value} onChange={(e) => onChange(e.target.value === "auto" ? null : Number(e.target.value))} className={`${inputCls} !w-auto !py-1.5`} data-testid="ms-current-gen">
        <option value="auto">Auto ({genLabel(spec.current_generation)})</option>
        {Array.from({ length: maxGen + 1 }, (_, i) => i + 1).map((g) => <option key={g} value={g}>Generation {g}</option>)}
      </select>
    </label>
  );
}

function GenTabs({ rows, active, onPick, onAdd }) {
  return (
    <div className="flex flex-wrap gap-2 mb-6" data-testid="ms-gen-tabs">
      {rows.map((r) => (
        <button key={r.generation} onClick={() => onPick(r.generation)} data-testid={`ms-gen-tab-${r.generation}`} className={`rounded-full px-4 py-2 text-sm border transition-colors ${active === r.generation ? "bg-[#73bf44] border-[#73bf44] text-[#050505] font-semibold" : "border-neutral-700 text-neutral-300 hover:border-neutral-500"}`}>
          {genLabel(r.generation)}{r.is_current ? " · Website" : ""} <span className="opacity-60">· {r.vans} vans</span>
          {!r.is_current && r.version && <span className="ml-1.5">✓</span>}
        </button>
      ))}
      <button onClick={onAdd} data-testid="ms-gen-add" className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm border border-dashed border-neutral-700 text-neutral-400 hover:text-[#73bf44] hover:border-[#73bf44]">
        <Plus size={14} /> Add generation
      </button>
    </div>
  );
}

function LinkedModels({ models }) {
  if (!models.length) return null;
  return (
    <div className="mt-8 text-xs text-neutral-500" data-testid="ms-linked-models">
      <div className="uppercase tracking-[0.15em] mb-2">Van models using these specs</div>
      <div className="flex flex-wrap gap-1.5">
        {models.map((m) => <span key={m.model} className="px-2 py-1 rounded border border-neutral-800 text-neutral-400">{m.model} · {m.vans}</span>)}
      </div>
    </div>
  );
}

function SpecEditor({ specId, onChanged, allSpecs }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [extra, setExtra] = useState([]);
  const load = () => adminApi.get(`/admin/model-specs/${specId}/generations`).then(({ data: d }) => {
    setData(d);
    setActive((a) => a ?? (d.generations.find((r) => !r.is_current) || d.generations[0]).generation);
  }).catch((e) => toast.error(getErrorMessage(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [specId]);
  if (!data) return <div className="text-neutral-600 p-6">Loading…</div>;

  const rows = [...data.generations, ...extra.filter((g) => !data.generations.some((r) => r.generation === g)).map((g) => ({ generation: g, is_current: false, vans: 0, version: null }))];
  const maxGen = Math.max(1, ...rows.map((r) => r.generation));
  const row = rows.find((r) => r.generation === active) || rows[0];
  const refresh = () => { load(); onChanged(); };
  const setCurrent = async (g) => {
    try { await adminApi.put(`/admin/model-specs/${specId}/current-generation`, { generation: g }); toast.success("Website generation updated"); setActive(null); setExtra([]); refresh(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  const addGen = () => { const g = maxGen + 1; setExtra([...extra, g]); setActive(g); };

  return (
    <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-6" data-testid="ms-editor">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="font-heading text-xl font-semibold text-white">{data.spec.name}</h2>
          <a href={data.spec.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#73bf44] mt-1">View on website <ArrowSquareOut size={11} /></a>
        </div>
        <GenerationPicker spec={data.spec} maxGen={maxGen} onChange={setCurrent} />
      </div>
      <GenTabs rows={rows} active={row.generation} onPick={setActive} onAdd={addGen} />
      <VersionEditor key={`${specId}-${row.generation}-${row.version?.updated_at || ""}-${row.is_current}`} spec={data.spec} row={row} onSaved={refresh} allSpecs={allSpecs} />
      <LinkedModels models={data.models} />
    </div>
  );
}

export default function AdminModelSpecs() {
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const load = () => adminApi.get("/admin/model-specs").then(({ data }) => {
    setList(data);
    setSelected((s) => s || data.specs[0]?.id || null);
  }).catch((e) => toast.error(getErrorMessage(e, "Couldn't load model specs")));
  useEffect(() => { load(); }, []);

  const refreshSite = async () => {
    try { await adminApi.post("/admin/model-specs/refresh"); toast.success("Reading the OTM website in the background…"); setTimeout(load, 4000); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div>
      <AdminHeader
        title="Model Specs"
        subtitle="Measurements, weights and standard features per model and generation. The website (MY26) fills one generation automatically each week; enter earlier or newer generations yourself. A van's own build details always win."
        actions={<GhostButton onClick={refreshSite} data-testid="ms-refresh"><ArrowsClockwise size={16} /> Refresh from website</GhostButton>}
      />
      <div className="p-8">
        {list?.state?.message && <p className="text-xs text-neutral-500 mb-4" data-testid="ms-sync-state">{list.state.message}</p>}
        {!list ? <div className="text-neutral-600">Loading…</div> : list.specs.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3"><Ruler size={36} className="text-neutral-700" /> No model specs yet. Refresh from the website.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 items-start">
            <ModelList specs={list.specs} selected={selected} onSelect={setSelected} />
            {selected && <SpecEditor key={selected} specId={selected} onChanged={load} allSpecs={list.specs} />}
          </div>
        )}
      </div>
    </div>
  );
}
